// Tiny YAML-frontmatter parser. We deliberately avoid pulling `gray-matter`
// into the browser bundle (it drags `js-yaml` and assumes Node). The SOP
// frontmatter contract is small and predictable, so a hand-rolled parser
// keeps the build lean and fully tree-shakable.
//
// Supported syntax (everything we use in content/_schema.md):
//   key: value
//   key: "quoted value"
//   key: [item1, item2, "item three"]
//   key:
//     - item
//     - item
//   key:
//     - title: "..."
//       url: "..."

export interface ParsedFrontmatter {
  data: Record<string, unknown>;
  body: string;
}

export function parseFrontmatter(raw: string): ParsedFrontmatter {
  const trimmed = raw.replace(/^﻿/, "");
  if (!trimmed.startsWith("---")) {
    return { data: {}, body: trimmed };
  }
  const end = trimmed.indexOf("\n---", 3);
  if (end === -1) {
    return { data: {}, body: trimmed };
  }
  const header = trimmed.slice(3, end).replace(/^\r?\n/, "");
  const body = trimmed.slice(end + 4).replace(/^\r?\n/, "");
  return { data: parseYamlSubset(header), body };
}

function parseYamlSubset(src: string): Record<string, unknown> {
  const lines = src.split(/\r?\n/);
  const root: Record<string, unknown> = {};
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line || /^\s*#/.test(line) || /^\s*$/.test(line)) {
      i++;
      continue;
    }
    const m = line.match(/^([A-Za-z0-9_-]+):\s*(.*)$/);
    if (!m) {
      i++;
      continue;
    }
    const key = m[1];
    const rest = m[2].trim();

    if (rest === "") {
      // Block-style value: either a list or a list-of-objects.
      const block: unknown[] = [];
      i++;
      while (i < lines.length) {
        const next = lines[i];
        const itemMatch = next.match(/^(\s+)-\s+(.*)$/);
        if (!itemMatch) break;
        const itemBody = itemMatch[2];
        // If the item is "key: value", collect this object's fields by reading
        // following indented lines.
        const objMatch = itemBody.match(/^([A-Za-z0-9_-]+):\s*(.*)$/);
        if (objMatch) {
          const obj: Record<string, unknown> = {};
          obj[objMatch[1]] = parseScalar(objMatch[2]);
          i++;
          while (i < lines.length) {
            const peek = lines[i];
            const sub = peek.match(/^(\s+)([A-Za-z0-9_-]+):\s*(.*)$/);
            if (!sub) break;
            // Sub-fields must be indented further than the dash.
            if (sub[1].length <= itemMatch[1].length) break;
            obj[sub[2]] = parseScalar(sub[3]);
            i++;
          }
          block.push(obj);
        } else {
          block.push(parseScalar(itemBody));
          i++;
        }
      }
      root[key] = block;
      continue;
    }

    root[key] = parseScalar(rest);
    i++;
  }

  return root;
}

function parseScalar(raw: string): unknown {
  const v = raw.trim();
  if (v === "") return "";
  if (v === "true") return true;
  if (v === "false") return false;
  if (v === "null" || v === "~") return null;
  // Quoted string.
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    return v.slice(1, -1).replace(/\\"/g, '"').replace(/\\'/g, "'");
  }
  // Inline list: [a, b, "c d"]
  if (v.startsWith("[") && v.endsWith("]")) {
    const inner = v.slice(1, -1).trim();
    if (inner === "") return [];
    return splitInlineList(inner).map(parseScalar);
  }
  // Number.
  if (/^-?\d+$/.test(v)) return parseInt(v, 10);
  if (/^-?\d+\.\d+$/.test(v)) return parseFloat(v);
  return v;
}

function splitInlineList(src: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let buf = "";
  let quote: string | null = null;
  for (const ch of src) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "[" || ch === "{") depth++;
    if (ch === "]" || ch === "}") depth--;
    if (ch === "," && depth === 0) {
      out.push(buf.trim());
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}
